import React, { useEffect, useRef } from 'react';
import { showToast } from '../../../stores/uiStore';

interface SyncProgressProps {
  domainId: string;
  syncStatus: string | null;
  filesTotal: number;
  filesProcessed: number;
  onRelearn?: () => void;
  errorCount?: number;
  syncedCount?: number;
}

export const SyncProgress: React.FC<SyncProgressProps> = ({
  domainId,
  syncStatus,
  filesTotal,
  filesProcessed,
  onRelearn,
  errorCount,
  syncedCount,
}) => {
  const prevStatusRef = useRef<string | null>(null);

  useEffect(() => {
    const prev = prevStatusRef.current;
    const isSyncComplete = syncStatus === 'synced' || syncStatus === 'completed';
    const wasSyncing = prev === 'syncing';
    if (isSyncComplete && wasSyncing) {
      showToast.success('ファイルの同期が完了しました');
    }
    prevStatusRef.current = syncStatus;
  }, [syncStatus]);

  if (syncStatus === 'syncing') {
    const progressValue = filesTotal > 0 ? Math.round((filesProcessed / filesTotal) * 100) : 0;
    return (
      <div className="mb-4 bg-white rounded-xl border border-border p-8 flex flex-col items-center text-center">
        {/* スピナーアイコン */}
        <div
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: '#E0F2F1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#0a7a72"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: '32px', height: '32px', animation: 'spin 1s linear infinite' }}
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </div>
        <p className="text-base font-bold text-text-primary mb-1">学習中…</p>
        <p className="text-sm text-text-secondary mb-6">ファイルを読み込んでいます</p>
        {/* プログレスバー */}
        <div className="w-full">
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2" data-testid="sync-progress-bar">
            <div
              className="h-2 rounded-full transition-all"
              style={{ width: `${progressValue}%`, backgroundColor: '#0a7a72' }}
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-text-secondary" data-testid="sync-progress-text">
              {filesProcessed} / {filesTotal} ファイル完了
            </p>
            <p className="text-xs font-bold" style={{ color: '#0a7a72' }}>{progressValue}%</p>
          </div>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (syncStatus === 'error') {
    return (
      <div className="mb-4 bg-white rounded-xl border border-red-200 p-6" data-testid="sync-error-panel">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-text-primary mb-1">一部のページの読み込みに失敗しました</p>
            {(syncedCount !== undefined || errorCount !== undefined) && (
              <p className="text-xs text-text-secondary mb-3" data-testid="sync-error-detail">
                {syncedCount !== undefined && `${syncedCount}件 学習済み`}
                {syncedCount !== undefined && errorCount !== undefined && ' / '}
                {errorCount !== undefined && `${errorCount}件 エラー`}
              </p>
            )}
            <p className="text-xs text-text-secondary mb-4">
              画像やPDF以外のファイルが含まれていた可能性があります。エラーのファイルを除外して再学習できます。
            </p>
            {onRelearn && (
              <button
                onClick={onRelearn}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#0a7a72' }}
                data-testid="sync-error-relearn-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                </svg>
                再学習する
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};
